import { useCallback, useMemo } from "react";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import { useMistralFirstInsights } from "@/hooks/useMistralFirstInsights";
import { parseInsightBullets } from "@/utils/parseInsightBullets";

const bulletAt = (section, index = 0) => {
  const bullets = parseInsightBullets(section);
  return bullets[index] || "";
};

const firstBullet = (section) => bulletAt(section, 0);

const headlineFrom = (text, fallback) => {
  const clean = String(text || "").trim();
  if (!clean) return fallback;
  const title = clean.split(/[:.]/)[0].trim();
  return title.length > 80 ? `${title.slice(0, 77)}…` : title;
};

/**
 * Mistral-first narrative overlay for executive and summary tabs.
 * Numeric KPIs stay rule-based; text fields prefer Mistral with local fallback.
 */
export function useMistralNarrativeOverlay({
  kind,
  title,
  buildLocalMarkdown,
  context = {},
  enabled = true,
  scopeKey,
}) {
  const insights = useMistralFirstInsights({
    kind,
    title,
    buildLocalMarkdown,
    context,
    enabled,
    scopeKey,
  });

  const buckets = useMemo(
    () => splitIntoInsightBuckets(insights.markdown),
    [insights.markdown]
  );

  const pickFromBucket = useCallback(
    (localValue, bucketKey, index = 0) => {
      if (insights.loading) return localValue;
      const picked = bulletAt(buckets[bucketKey], index);
      return picked || localValue;
    },
    [insights.loading, buckets]
  );

  const pickHeadline = useCallback(
    (localHeadline) => {
      if (insights.loading) return localHeadline;
      const text = firstBullet(buckets.insights) || firstBullet(buckets.recs);
      return text ? headlineFrom(text, localHeadline) : localHeadline;
    },
    [insights.loading, buckets]
  );

  const pickPrimaryAction = useCallback(
    (localAction) => {
      if (insights.loading) return localAction;
      return (
        firstBullet(buckets.actions) ||
        firstBullet(buckets.recs) ||
        bulletAt(buckets.insights, 1) ||
        localAction
      );
    },
    [insights.loading, buckets]
  );

  const pickInsightsBullets = useCallback(
    (localBullets = []) => {
      if (insights.loading) return [];
      const aiBullets = parseInsightBullets(buckets.insights);
      return aiBullets.length ? aiBullets : localBullets;
    },
    [insights.loading, buckets.insights]
  );

  const pickBulletList = useCallback(
    (localBullets = [], bucketKey = "insights") => {
      if (insights.loading) return localBullets;
      const bullets = parseInsightBullets(buckets[bucketKey]);
      return bullets.length ? bullets : localBullets;
    },
    [insights.loading, buckets]
  );

  const pickMapped = useCallback(
    (localItems = [], bucketKey = "insights", mapper) => {
      if (insights.loading) return localItems;
      if (typeof mapper !== "function") return localItems;
      const bullets = parseInsightBullets(buckets[bucketKey]);
      if (!bullets.length) return localItems;
      const mapped = mapper(bullets, localItems);
      return mapped?.length ? mapped : localItems;
    },
    [insights.loading, buckets]
  );

  return {
    ...insights,
    buckets,
    pickFromBucket,
    pickHeadline,
    pickPrimaryAction,
    pickInsightsBullets,
    pickBulletList,
    pickMapped,
  };
}
